/**
 * ReengagementSettingsPage: admin-only configuration of the discharged-client
 * re-engagement rule (the gate that unlocks the "we couldn't reach you" email).
 *
 * Route: /discharged-reengagement-settings (RequireRole ['super_admin','administrator'])
 * Backend: getReengagementConfig / updateReengagementConfig callables (same role
 * gate server-side). Thin editor over companies/{c}/discharged_reengagement_config/settings.
 *
 * The send email stays locked until a broker logs >= minAttempts phone contacts
 * across >= minWindowDays, measured by server-stamped contact time, and (when
 * requireDistinctDays is on) across >= minAttempts distinct days, so a burst of
 * calls in one sitting cannot unlock it. Defaults: 4 attempts / 21 days.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import {
  PhoneOutgoing,
  Save,
  Loader2,
  ShieldCheck,
  Building2,
  ArrowLeft,
  Eye,
  Send,
  Globe,
  ExternalLink,
  Plus,
  Trash2,
  Palette,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { useReengagementConfig } from '../../hooks/useReengagementConfig';
import {
  REENGAGEMENT_RULE_DEFAULTS,
  REENGAGEMENT_CONSENT_BASIS_LABELS,
  REENGAGEMENT_DEFAULT_COPY,
  REENGAGEMENT_VARIANT_LABELS,
  type DischargedReengagementConfig,
  type ReengagementConsentBasis,
  type ReengagementFromAddressMode,
  type ReengagementVariant,
  type ReengagementTemplateOverride,
  type ReengagementServiceItem,
  type ReengagementTrustStat,
} from '../../types/dischargedReengagement';

type Draft = DischargedReengagementConfig;

function buildDraft(config: DischargedReengagementConfig | null): Draft {
  return {
    enabled: config?.enabled ?? false,
    minAttempts: config?.minAttempts ?? REENGAGEMENT_RULE_DEFAULTS.minAttempts,
    minWindowDays: config?.minWindowDays ?? REENGAGEMENT_RULE_DEFAULTS.minWindowDays,
    requireDistinctDays: config?.requireDistinctDays ?? REENGAGEMENT_RULE_DEFAULTS.requireDistinctDays,
    minDaysBetweenReengagementEmails:
      config?.minDaysBetweenReengagementEmails ??
      REENGAGEMENT_RULE_DEFAULTS.minDaysBetweenReengagementEmails,
    fromAddressMode: config?.fromAddressMode ?? REENGAGEMENT_RULE_DEFAULTS.fromAddressMode,
    maxSendsPerBrokerPerDay:
      config?.maxSendsPerBrokerPerDay ?? REENGAGEMENT_RULE_DEFAULTS.maxSendsPerBrokerPerDay,
    crossSellBlurb: config?.crossSellBlurb ?? null,
    defaultConsentBasis: config?.defaultConsentBasis ?? REENGAGEMENT_RULE_DEFAULTS.defaultConsentBasis,
    senderLegalName: config?.senderLegalName ?? '',
    senderAbn: config?.senderAbn ?? '',
    senderPostalAddress: config?.senderPostalAddress ?? '',
    senderContactPhone: config?.senderContactPhone ?? '',
    senderContactEmail: config?.senderContactEmail ?? '',
    templates: config?.templates ?? {},
    emailAccentColor: config?.emailAccentColor ?? null,
    emailTagline: config?.emailTagline ?? null,
    emailServices: config?.emailServices ?? [],
    emailTrustStats: config?.emailTrustStats ?? [],
    emailLocations: config?.emailLocations ?? null,
  };
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw.trim());
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-6 space-y-4';
const INPUT =
  'w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none';
const LABEL = 'block text-sm font-medium text-gray-300 mb-1';

const CONSENT_OPTIONS: ReengagementConsentBasis[] = [
  'existing_business_relationship',
  'express_consent',
  'manual_override',
];

export default function ReengagementSettingsPage() {
  const { userData } = useOutletContext<{ userData: { companyId?: string } | null }>();
  const companyId = userData?.companyId;

  const { config, loading, saving, error, save } = useReengagementConfig(companyId);
  const [draft, setDraft] = useState<Draft>(() => buildDraft(null));

  useEffect(() => {
    if (config) setDraft(buildDraft(config));
  }, [config]);

  const set = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  const setTemplate = useCallback(
    (variant: ReengagementVariant, field: keyof ReengagementTemplateOverride, value: string) => {
      setDraft((d) => {
        const templates = { ...(d.templates ?? {}) };
        const current = { ...(templates[variant] ?? {}) };
        if (value.trim()) current[field] = value;
        else delete current[field]; // empty -> fall back to default
        templates[variant] = current;
        return { ...d, templates };
      });
    },
    [],
  );

  const updateService = useCallback(
    (i: number, field: keyof ReengagementServiceItem, value: string) => {
      setDraft((d) => {
        const list = [...(d.emailServices ?? [])];
        list[i] = { ...(list[i] ?? { title: '', description: '' }), [field]: value };
        return { ...d, emailServices: list };
      });
    },
    [],
  );
  const addService = useCallback(() => {
    setDraft((d) => ({
      ...d,
      emailServices: [...(d.emailServices ?? []), { title: '', description: '' }],
    }));
  }, []);
  const removeService = useCallback((i: number) => {
    setDraft((d) => ({ ...d, emailServices: (d.emailServices ?? []).filter((_, idx) => idx !== i) }));
  }, []);

  const updateStat = useCallback(
    (i: number, field: keyof ReengagementTrustStat, value: string) => {
      setDraft((d) => {
        const list = [...(d.emailTrustStats ?? [])];
        list[i] = { ...(list[i] ?? { value: '', label: '' }), [field]: value };
        return { ...d, emailTrustStats: list };
      });
    },
    [],
  );
  const addStat = useCallback(() => {
    setDraft((d) => ({
      ...d,
      emailTrustStats: [...(d.emailTrustStats ?? []), { value: '', label: '' }],
    }));
  }, []);
  const removeStat = useCallback((i: number) => {
    setDraft((d) => ({ ...d, emailTrustStats: (d.emailTrustStats ?? []).filter((_, idx) => idx !== i) }));
  }, []);

  const [previewVariant, setPreviewVariant] = useState<ReengagementVariant>('voicemail_followup');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const runPreview = useCallback(
    async (variant: ReengagementVariant) => {
      if (!companyId) return;
      setPreviewVariant(variant);
      setPreviewLoading(true);
      try {
        const fn = httpsCallable(functions, 'previewReengagementEmail');
        const res = (
          await fn({ companyId, templateKey: variant, copyOverride: draft.templates?.[variant] ?? {} })
        ).data as { subject: string; html: string };
        setPreviewSubject(res.subject);
        setPreviewHtml(res.html);
      } catch {
        toast.error('Could not render preview');
        setPreviewHtml(null);
      } finally {
        setPreviewLoading(false);
      }
    },
    [companyId, draft.templates],
  );

  const sendTest = useCallback(
    async (variant: ReengagementVariant) => {
      if (!companyId) return;
      setTestSending(true);
      try {
        const fn = httpsCallable(functions, 'sendReengagementTestEmail');
        const res = (
          await fn({ companyId, templateKey: variant, copyOverride: draft.templates?.[variant] ?? {} })
        ).data as { ok: boolean; to: string };
        toast.success(`Test email sent to ${res.to}`);
      } catch (e) {
        const code = (e as { message?: string })?.message || '';
        toast.error(
          /resend_domain_not_verified|resend_key/.test(code)
            ? 'Set up your Resend sending domain first (see the panel below)'
            : 'Could not send test email',
        );
      } finally {
        setTestSending(false);
      }
    },
    [companyId, draft.templates],
  );

  const senderComplete = useMemo(
    () =>
      Boolean(
        draft.senderLegalName.trim() &&
          draft.senderPostalAddress.trim() &&
          draft.senderContactPhone.trim() &&
          draft.senderContactEmail.trim(),
      ),
    [draft],
  );

  const onSave = useCallback(async () => {
    if (draft.enabled && !senderComplete) {
      toast.error('Sender identity (name, address, phone, email) is required before enabling.');
      return;
    }
    const ok = await save(draft);
    toast[ok ? 'success' : 'error'](
      ok ? 'Re-engagement settings saved' : 'Could not save settings',
    );
  }, [draft, save, senderComplete]);

  if (!companyId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-gray-400">
        No company in scope.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Link
        to="/reports/discharged-loans"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Discharged Loans
      </Link>
      <PageHeader
        icon={PhoneOutgoing}
        title="Discharged Re-Engagement"
        subtitle="Rules for the post-discharge calling campaign and the follow-up email gate"
        actions={
          <button
            type="button"
            onClick={onSave}
            disabled={saving || loading}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Enable + gate rule */}
      <div className={CARD}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-100">Feature</h2>
            <p className="text-sm text-gray-400">
              When off, the call list and send button are hidden for everyone in this company.
            </p>
          </div>
          <Toggle
            checked={draft.enabled}
            onChange={(v) => set('enabled', v)}
            label="Enable discharged re-engagement"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 pt-2">
          <div>
            <label className={LABEL}>Minimum attempts</label>
            <input
              type="number"
              min={1}
              max={20}
              value={draft.minAttempts}
              onChange={(e) => set('minAttempts', clampInt(e.target.value, 1, 20, 4))}
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Over a minimum of (days)</label>
            <input
              type="number"
              min={0}
              max={180}
              value={draft.minWindowDays}
              onChange={(e) => set('minWindowDays', clampInt(e.target.value, 0, 180, 21))}
              className={INPUT}
            />
            <p className="mt-1 text-xs text-gray-400">
              Days between the first and last attempt. 0 removes the requirement, so attempts made
              in one sitting can unlock.
            </p>
            {draft.minWindowDays >= 1 && draft.minAttempts < 2 && (
              <p className="mt-1 text-xs text-amber-400">
                A span of {draft.minWindowDays} day{draft.minWindowDays === 1 ? '' : 's'} needs at
                least 2 attempts on different days, so the effective minimum is 2, not{' '}
                {draft.minAttempts}. Set this to 0 if one attempt should be enough.
              </p>
            )}
          </div>
          <div>
            <label className={LABEL}>Cooldown between emails (days)</label>
            <input
              type="number"
              min={0}
              max={365}
              value={draft.minDaysBetweenReengagementEmails}
              onChange={(e) =>
                set('minDaysBetweenReengagementEmails', clampInt(e.target.value, 0, 365, 90))
              }
              className={INPUT}
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-gray-900/60 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-200">Require attempts on distinct days</p>
            <p className="text-xs text-gray-400">
              Stops a burst of calls in one sitting from unlocking the email. Measured by
              server-recorded contact time, so it cannot be backdated.
            </p>
          </div>
          <Toggle
            checked={draft.requireDistinctDays}
            onChange={(v) => set('requireDistinctDays', v)}
            label="Require distinct days"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={LABEL}>Max sends per broker per day</label>
            <input
              type="number"
              min={1}
              max={500}
              value={draft.maxSendsPerBrokerPerDay}
              onChange={(e) => set('maxSendsPerBrokerPerDay', clampInt(e.target.value, 1, 500, 25))}
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Default consent basis</label>
            <select
              value={draft.defaultConsentBasis}
              onChange={(e) =>
                set('defaultConsentBasis', e.target.value as ReengagementConsentBasis)
              }
              className={INPUT}
            >
              {CONSENT_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {REENGAGEMENT_CONSENT_BASIS_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* From address */}
      <div className={CARD}>
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-blue-400" />
          <h2 className="text-base font-semibold text-gray-100">Sending address</h2>
        </div>
        <p className="text-sm text-gray-400">
          "Broker" sends from each broker's own address on your verified domain (best reply rate;
          requires the domain verified in Resend). "No-reply" sends from your verified no-reply
          address.
        </p>
        <div className="flex gap-3">
          {(['broker', 'noreply'] as ReengagementFromAddressMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => set('fromAddressMode', mode)}
              className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
                draft.fromAddressMode === mode
                  ? 'border-blue-500 bg-blue-500/10 text-blue-200'
                  : 'border-gray-600 bg-gray-900 text-gray-300 hover:border-gray-500'
              }`}
            >
              {mode === 'broker' ? "Broker's own address" : 'No-reply address'}
            </button>
          ))}
        </div>
      </div>

      {/* Sender identity (Spam Act s.17) */}
      <div className={CARD}>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-blue-400" />
          <h2 className="text-base font-semibold text-gray-100">Sender identity</h2>
        </div>
        <p className="text-sm text-gray-400">
          Required by the Spam Act before any email can be sent. Appears in the email footer.
          {draft.enabled && !senderComplete && (
            <span className="ml-1 text-amber-300">Complete these to enable sending.</span>
          )}
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={LABEL}>Legal / business name</label>
            <input
              type="text"
              value={draft.senderLegalName}
              onChange={(e) => set('senderLegalName', e.target.value)}
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>ABN (optional)</label>
            <input
              type="text"
              value={draft.senderAbn ?? ''}
              onChange={(e) => set('senderAbn', e.target.value)}
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Contact phone</label>
            <input
              type="text"
              value={draft.senderContactPhone}
              onChange={(e) => set('senderContactPhone', e.target.value)}
              className={INPUT}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL}>Postal / contact address</label>
            <input
              type="text"
              value={draft.senderPostalAddress}
              onChange={(e) => set('senderPostalAddress', e.target.value)}
              className={INPUT}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL}>Contact email</label>
            <input
              type="email"
              value={draft.senderContactEmail}
              onChange={(e) => set('senderContactEmail', e.target.value)}
              className={INPUT}
            />
          </div>
        </div>
      </div>

      {/* Cross-sell blurb */}
      <div className={CARD}>
        <h2 className="text-base font-semibold text-gray-100">Services blurb (optional)</h2>
        <p className="text-sm text-gray-400">
          An "everything we do" block appended to the email body. Leave blank to use the template
          default.
        </p>
        <textarea
          rows={4}
          value={draft.crossSellBlurb ?? ''}
          onChange={(e) => set('crossSellBlurb', e.target.value || null)}
          className={`${INPUT} resize-y`}
          placeholder="Home & property finance, buyers advocacy, asset finance, conveyancing…"
        />
      </div>

      {/* Email content & branding (rich, self-serve) */}
      <div className={CARD}>
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-100">
          <Palette className="h-4 w-4 text-blue-400" /> Email content &amp; branding
        </h2>
        <p className="text-sm text-gray-400">
          Make the email yours: an accent colour, a tagline, a "what we can help with" services
          grid, trust stats and your locations. The logo and primary colour come from your Lead
          Capture branding. Leave a section empty to hide it.
        </p>

        {/* Accent colour */}
        <div>
          <label className={LABEL}>Button accent colour</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={draft.emailAccentColor || '#2563eb'}
              onChange={(e) => set('emailAccentColor', e.target.value)}
              className="h-9 w-12 rounded border border-gray-600 bg-gray-900"
            />
            <input
              type="text"
              value={draft.emailAccentColor ?? ''}
              placeholder="#13af7a"
              onChange={(e) => set('emailAccentColor', e.target.value || null)}
              className={`${INPUT} max-w-[160px]`}
            />
          </div>
        </div>

        {/* Tagline */}
        <div>
          <label className={LABEL}>Tagline (under the logo)</label>
          <input
            type="text"
            value={draft.emailTagline ?? ''}
            placeholder="Finance, Property, Legal"
            onChange={(e) => set('emailTagline', e.target.value || null)}
            className={INPUT}
          />
        </div>

        {/* Services grid */}
        <div>
          <label className={LABEL}>Services grid ("what we can help with")</label>
          <p className="mb-2 text-xs text-gray-500">
            When you add services, this grid replaces the plain blurb above. Up to 6.
          </p>
          <div className="space-y-2">
            {(draft.emailServices ?? []).map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-lg border border-gray-700 bg-gray-900/40 p-3"
              >
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    value={s.title}
                    placeholder="Home & Property Finance"
                    onChange={(e) => updateService(i, 'title', e.target.value)}
                    className={INPUT}
                  />
                  <textarea
                    rows={2}
                    value={s.description}
                    placeholder="First home, refinancing, investment, commercial and more."
                    onChange={(e) => updateService(i, 'description', e.target.value)}
                    className={`${INPUT} resize-y`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeService(i)}
                  className="mt-1 rounded p-1.5 text-gray-400 hover:bg-gray-700 hover:text-red-400"
                  aria-label="Remove service"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {(draft.emailServices ?? []).length < 6 && (
            <button
              type="button"
              onClick={addService}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-600 bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" /> Add service
            </button>
          )}
        </div>

        {/* Trust stats */}
        <div>
          <label className={LABEL}>Trust stats (optional)</label>
          <div className="space-y-2">
            {(draft.emailTrustStats ?? []).map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={s.value}
                  placeholder="$3B+"
                  onChange={(e) => updateStat(i, 'value', e.target.value)}
                  className={`${INPUT} max-w-[120px]`}
                />
                <input
                  type="text"
                  value={s.label}
                  placeholder="Loans settled"
                  onChange={(e) => updateStat(i, 'label', e.target.value)}
                  className={INPUT}
                />
                <button
                  type="button"
                  onClick={() => removeStat(i)}
                  className="rounded p-1.5 text-gray-400 hover:bg-gray-700 hover:text-red-400"
                  aria-label="Remove stat"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          {(draft.emailTrustStats ?? []).length < 4 && (
            <button
              type="button"
              onClick={addStat}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-600 bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" /> Add stat
            </button>
          )}
        </div>

        {/* Locations */}
        <div>
          <label className={LABEL}>Locations (footer)</label>
          <input
            type="text"
            value={draft.emailLocations ?? ''}
            placeholder="Melbourne, Mornington, Byron Bay, Brisbane"
            onChange={(e) => set('emailLocations', e.target.value || null)}
            className={INPUT}
          />
        </div>
      </div>

      {/* Email templates (self-serve copy editor) */}
      <div className={CARD}>
        <h2 className="text-base font-semibold text-gray-100">Email templates</h2>
        <p className="text-sm text-gray-400">
          Edit the wording of the two emails. Leave a field blank to use the default shown.
          Merge tags: <code className="text-gray-300">{'{firstName}'}</code>,{' '}
          <code className="text-gray-300">{'{callerName}'}</code>,{' '}
          <code className="text-gray-300">{'{companyName}'}</code>. The branded layout, header and
          unsubscribe footer stay fixed.
        </p>
        {(['voicemail_followup', 'after_conversation'] as ReengagementVariant[]).map((variant) => {
          const t = draft.templates?.[variant] ?? {};
          const d = REENGAGEMENT_DEFAULT_COPY[variant];
          return (
            <div
              key={variant}
              className="space-y-3 rounded-lg border border-gray-700 bg-gray-900/40 p-4"
            >
              <h3 className="text-sm font-semibold text-blue-300">
                {REENGAGEMENT_VARIANT_LABELS[variant]}
              </h3>
              <div>
                <label className={LABEL}>Subject line</label>
                <input
                  type="text"
                  value={t.subject ?? ''}
                  placeholder={d.subject}
                  onChange={(e) => setTemplate(variant, 'subject', e.target.value)}
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL}>Heading</label>
                <input
                  type="text"
                  value={t.heading ?? ''}
                  placeholder={d.heading}
                  onChange={(e) => setTemplate(variant, 'heading', e.target.value)}
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL}>Intro paragraph</label>
                <textarea
                  rows={3}
                  value={t.intro ?? ''}
                  placeholder={d.intro}
                  onChange={(e) => setTemplate(variant, 'intro', e.target.value)}
                  className={`${INPUT} resize-y`}
                />
              </div>
              <div>
                <label className={LABEL}>Body paragraph</label>
                <textarea
                  rows={2}
                  value={t.body ?? ''}
                  placeholder={d.body}
                  onChange={(e) => setTemplate(variant, 'body', e.target.value)}
                  className={`${INPUT} resize-y`}
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Button label</label>
                  <input
                    type="text"
                    value={t.ctaLabel ?? ''}
                    placeholder={d.ctaLabel}
                    onChange={(e) => setTemplate(variant, 'ctaLabel', e.target.value)}
                    className={INPUT}
                  />
                </div>
                <div>
                  <label className={LABEL}>Sign-off</label>
                  <textarea
                    rows={2}
                    value={t.signOff ?? ''}
                    placeholder={d.signOff}
                    onChange={(e) => setTemplate(variant, 'signOff', e.target.value)}
                    className={`${INPUT} resize-y`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Preview & test */}
      <div className={CARD}>
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-100">
          <Eye className="h-4 w-4 text-blue-400" /> Preview &amp; test
        </h2>
        <p className="text-sm text-gray-400">
          See exactly what the email looks like with your branding and current copy (including any
          unsaved edits above). A test send goes to your own inbox and needs your Resend sending
          domain set up first.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {(['voicemail_followup', 'after_conversation'] as ReengagementVariant[]).map((variant) => (
            <button
              key={variant}
              type="button"
              onClick={() => runPreview(variant)}
              disabled={previewLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm font-medium text-gray-200 hover:bg-gray-700 disabled:opacity-50"
            >
              <Eye className="h-4 w-4" /> Preview: {REENGAGEMENT_VARIANT_LABELS[variant]}
            </button>
          ))}
          <button
            type="button"
            onClick={() => sendTest(previewVariant)}
            disabled={testSending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {testSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send test to me
          </button>
        </div>
        {previewHtml && (
          <p className="text-xs text-gray-500">
            Test send will use the {REENGAGEMENT_VARIANT_LABELS[previewVariant]} email.
          </p>
        )}
        {previewLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Rendering preview…
          </div>
        )}
        {previewHtml && !previewLoading && (
          <div className="space-y-2">
            <div className="text-sm text-gray-400">
              <span className="font-medium text-gray-300">Subject:</span> {previewSubject}
            </div>
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="h-[640px] w-full rounded-lg border border-gray-700 bg-white"
              sandbox=""
            />
          </div>
        )}
      </div>

      {/* Sending domain (Resend / DNS) guide */}
      <div className={CARD}>
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-100">
          <Globe className="h-4 w-4 text-blue-400" /> Set up your sending domain (Resend)
        </h2>
        <p className="text-sm text-gray-400">
          To send from your own address (e.g.{' '}
          <code className="text-gray-300">name@yourdomain.com.au</code>), your domain has to be
          verified in Resend. These records authorise sending; they do not change where your mail is
          delivered.
        </p>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-300">
          <li>In Resend, add your domain and select the records for your visible "from" domain.</li>
          <li>
            Add the records Resend shows you to your DNS host:
            <ul className="mt-1 list-disc space-y-1 pl-5 text-gray-400">
              <li>
                <span className="font-medium text-gray-300">DKIM</span>: CNAME record(s) that sign
                your mail.
              </li>
              <li>
                <span className="font-medium text-gray-300">Return-path / SPF</span>: live on
                Resend's <code className="text-gray-300">send.</code> subdomain (its own SPF and MX).
                Your root SPF and your Google MX stay untouched.
              </li>
            </ul>
          </li>
          <li>
            Wait for Resend to mark the domain{' '}
            <span className="font-medium text-emerald-300">Verified</span> (usually minutes, up to a
            few hours).
          </li>
          <li>
            Tell us the verified domain so we wire it to your account, then use "Send test to me"
            above to confirm it works.
          </li>
        </ol>
        <a
          href="https://resend.com/docs/dashboard/domains/introduction"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-400 hover:text-blue-300"
        >
          Resend domain setup docs <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}
